/* @flow */
import * as React from 'react';
import { observer, inject } from 'mobx-react';
import { reaction, computed } from 'mobx';
import { translate } from 'react-i18next';
import { Link } from 'react-router-dom';

import type { TranslatorProps } from 'react-i18next';
import { isIE } from 'utils/browserDetection';
import navigation from 'services/NavigationService';
import DashboardStore from 'stores/DashboardStore';
import { ComponentWithInjectedProps } from 'utils/injectedProps';

import NivoBarChart from '../../components/NivoBarChart';
import type { CommonWidgetProps } from '../../commonTypes';

const STATIC_DATA = [
  {
    id: 'static-1',
    name: 'ut similique provident',
    approved_rented_assets_sum: 12876,
    approved_owned_assets_sum: 3763,
    rented_asset_count: 19,
    owned_asset_count: 107,
    project_ids: ['4c30a76c-1f3a-4164-b58c-9f9445439c2c'],
  },
  {
    id: 'static-2',
    name: 'ut similique provident',
    approved_rented_assets_sum: 54728,
    approved_owned_assets_sum: 23432,
    rented_asset_count: 10,
    owned_asset_count: 45,
    project_ids: ['4c30a76c-1f3a-4164-b58c-9f9445439c2c'],
  },
  {
    id: 'static-3',
    name: 'ut similique provident',
    approved_rented_assets_sum: 10993.59,
    approved_owned_assets_sum: 64471.75,
    rented_asset_count: 19,
    owned_asset_count: 107,
    project_ids: ['4c30a76c-1f3a-4164-b58c-9f9445439c2c'],
  },
];

export type CostCentersProps = CommonWidgetProps;

type InjectedProps = {
  dashboardStore: DashboardStore,
} & TranslatorProps;

function firstToUpperCase(str) {
  return str.toUpperCase();
}

@translate()
@inject('dashboardStore')
@observer
export default class CostCenters extends ComponentWithInjectedProps<
  CostCentersProps,
  {},
  InjectedProps
> {
  static LOAD_THUMBNAIL = false;

  @computed get shouldLoadData(): boolean {
    const { size } = this.props;

    return size !== 'thumbnail' || CostCenters.LOAD_THUMBNAIL;
  }

  @computed get widgetData(): any[] | null {
    const { dashboardStore } = this.props;

    if (!this.shouldLoadData) {
      return STATIC_DATA;
    }

    return dashboardStore.costcenters ? dashboardStore.costcenters['cost-centres'] : null;
  }

  autoUpdateDisposer: Function;

  componentDidMount() {
    const { dashboardStore } = this.props;

    this.loadWidgetData(dashboardStore.loadedTradingAccount);

    this.autoUpdateDisposer = reaction(
      () => dashboardStore.loadedTradingAccount,
      ta => {
        this.loadWidgetData(ta);
      }
    );
  }

  loadWidgetData = (tradingAccount: string) => {
    const { dashboardStore } = this.props;

    if (this.shouldLoadData) {
      dashboardStore.loadCostCenters(tradingAccount);
    }
  };

  componentWillUnmount() {
    this.autoUpdateDisposer();
  }

  render() {
    const { dashboardStore, t, widgetItem, size, isFullScreen } = this.props;
    const accountId = dashboardStore.loadedTradingAccount;

    const costCenterData = this.widgetData
      ? this.widgetData
          .map((center, index) => {
            if (!center.approved_owned_assets_sum && !center.approved_rented_assets_sum) {
              return null;
            }

            return {
              cost_center: center.name,
              cost_center_id: center.id,
              owned: Number(Number(center.approved_owned_assets_sum).toFixed(2)),
              owned_color: 'hsl(203, 54%, 46%)',
              owned_number: center.owned_asset_count,
              rented: Number(Number(center.approved_rented_assets_sum).toFixed(2)),
              rented_color: 'hsl(200, 92%, 79%)',
              rented_number: center.rented_asset_count,
              projects: center.project_ids,
            };
          })
          .filter(obj => obj !== null)
      : null;

    return (
      <NivoBarChart
        widgetInfos={[{ title: t('general:dashboards.assets_by_cost_center'), variant: 'default' }]}
        widgetItem={widgetItem}
        size={size}
        isFullScreen={isFullScreen}
        isIE={isIE}
        data={costCenterData}
        isLoading={this.shouldLoadData && dashboardStore.loadingCostCenters}
        legendKeys={[
          {
            label: t('general:assets.owned_asset_plural'),
            color: 'hsl(203, 54%, 46%)',
          },
          {
            label: t('general:assets.rented_asset_plural'),
            color: 'hsl(200, 92%, 79%)',
          },
        ]}
        button={true}
        buttonLink={`/cost-centers/account/${accountId}`}
        buttonText={t('general:actions.view_cost_center_plural')}
        layout="vertical"
        // Nivo bar chart props
        indexBy="cost_center_id"
        keys={['owned', 'rented']}
        colorBy={function(e: any): string {
          return e.data[e.id + '_color'];
        }}
        axisLeft={{
          renderTick: tick => {
            return (
              <g key={tick.key} transform={`translate(${tick.x},${tick.y})`}>
                <text
                  textAnchor="end"
                  alignmentBaseline="central"
                  transform={`translate(${tick.textX}, ${tick.textY})`}
                >
                  £{tick.value}
                </text>
              </g>
            );
          },
        }}
        axisBottom={{
          renderTick: tick => {
            const costCenterName: string = costCenterData[tick.tickIndex]
              ? costCenterData[tick.tickIndex].cost_center
              : '';

            const abbrvName: string =
              costCenterName.length > 12 ? costCenterName.substring(0, 12) + '...' : costCenterName;

            return (
              <g key={tick.key} transform={`translate(${tick.x},${tick.y})`}>
                <text
                  textAnchor="end"
                  alignmentBaseline="central"
                  transform={`translate(${tick.textX},
                                        ${tick.textY}) rotate(-60)`}
                >
                  <Link to={`/cost-centers/${tick.key}`}>{abbrvName}</Link>
                </text>
              </g>
            );
          },
        }}
        onClick={e => {
          const assetType = e.id === 'rented' ? 'rented open' : e.id;
          navigation.navigateTo(`/assets/${assetType}/project/${e.data.projects.join('|')}`);
        }}
        tooltip={function(data) {
          return (
            <div>
              <b>{data.data.cost_center}</b>
              <br />
              {`${data.id.replace(/\b[a-z]/, firstToUpperCase)} assets value: `}
              <b>{`£${data.value}`}</b>
              <br />
              {`No. of ${data.id.replace(/\b[a-z]/, firstToUpperCase)} assets: `}
              <b>{data.id === 'rented' ? data.data.rented_number : data.data.owned_number}</b>
            </div>
          );
        }}
      />
    );
  }
}
